import React from "react";
import { useQuery } from "@tanstack/react-query";
import Text from "./Text";
import Title from "./Title";
import Video from "./Video";
import Button from "./Button";
import Image from "./Image";
import File from "./File";
import ImageCollection from "./ImageCollection";
import Hero from "./Hero";
import ButtonCollection from "./ButtonCollection";
import EditableElement from "../../editor/components/EditableElement";
import { apiFetch } from "../../core/utils/api";
import DefaultItem from "./DefaultItem";
import { usePreviewOptional } from "../../editor/context/PreviewContext";

const isHydratedItem = (type, data) => {
  if (!type || !data) return false;
  switch (type) {
    case "title":
      // Title renderer requires `text`
      return Object.prototype.hasOwnProperty.call(data, "text");
    case "text":
      // Text renderer requires `rendered_content`
      return Object.prototype.hasOwnProperty.call(data, "rendered_content");
    case "button":
      // Button renderer uses nested Text item with rendered_content
      return !!data.text && Object.prototype.hasOwnProperty.call(data.text, "rendered_content");
    case "button_collection":
      // Collection renderer needs `buttons` array to show children without a full reload
      return Array.isArray(data.buttons);
    case "image":
    case "video":
    case "image_collection":
      // Backend's minimal child refs (used by Hero/Component when not in preview)
      // include `image_url`/`alt_text` but intentionally omit styling fields like
      // `format`, `container_style`, `max_width_pct_component`, `position`, etc.
      // The full serializers always include `version`, so use it as the hydration
      // discriminator. Without this, partial refs would seed the React Query cache
      // as fresh data and the renderer would paint without rounded borders, shape
      // formatting, or sizing until staleTime expired.
      return Object.prototype.hasOwnProperty.call(data, "version");
    default:
      // For other types we accept whatever the server returns
      return true;
  }
};

const Item = ({ item, itemRef, disableQuickDelete = false }) => {
  // If we received only a minimal ref, fetch full item via typed endpoint
  // Prefer explicit `item` when both `item` and `itemRef` are provided.
  // This avoids accidentally fetching/routing based on a stale `itemRef`.
  const type = item?.type ?? itemRef?.type;
  const id = item?.id ?? itemRef?.id;
  const canUseInitialData = !!item && isHydratedItem(type, item);
  const preview = usePreviewOptional();
  const shouldDisableFetch = Boolean(preview?.isPresetStylePreview);

  const { data: fetchedItem, isLoading, error } = useQuery({
    queryKey: ["item", type, id],
    queryFn: async () => {
      const response = await apiFetch(`/api/items/${type}/${id}/`);
      if (!response.ok) {
        throw new Error(`Error fetching ${type} ${id}: ${response.status}`);
      }
      const data = await response.json();
      return data;
    },
    // Always enable query when we have type and id, so React Query can auto-refetch on invalidation
    enabled: !!type && !!id && !shouldDisableFetch,
    staleTime: 30 * 1000,
    keepPreviousData: true,
    // Use initialData if full item was provided (avoids unnecessary fetch on mount)
    initialData: canUseInitialData ? item : undefined,
  });

  if (!item && !itemRef) return null;
  if (isLoading && !fetchedItem && !canUseInitialData) return <div className="h-8 w-full" />;
  // If we have any previously-fetched data, keep rendering it even if the latest refetch errored
  // (e.g., transient network loss). Only show an error when we have nothing to render.
  if (error && !fetchedItem && !canUseInitialData)
    return <div className="text-red-600">Failed to load item</div>;

  const effective = fetchedItem || item || itemRef || null;
  if (!effective) return null;

  // If we still don't have enough fields to render, keep showing a lightweight placeholder
  if (!isHydratedItem(type, effective) && isLoading) return <div className="h-8 w-full" />;
  
  // Render the appropriate component based on item type
  const renderItem = () => {
    
    switch (effective.type) {
      case "text":
        return <Text item={effective} />;
      case "title":
        return <Title item={effective} />;
      case "video":
        return <Video item={effective} />;
      case "button":
        return <Button item={effective} />;
      case "image":
        return <Image item={effective} />;
      case "file":
        return <File item={effective} />;
      case "image_collection":
        return <ImageCollection item={effective} />;
      case "hero":
        return <Hero item={effective} />;
      case "button_collection":
        return <ButtonCollection item={effective} itemRef={itemRef} />;
      default:
        return <DefaultItem item={effective} />;
    }
  };

  return (
    <EditableElement element={effective} disableQuickDelete={disableQuickDelete}>
      {renderItem()}
    </EditableElement>
  );
};

export default Item;
